import React, { useEffect, useState } from 'react';

// ZigZagPath
// Props:
// - levels: number of nodes
// - target: 1-based level index to highlight
// - highlightColor: color for highlighted node
// - size / spacing: layout tweaks
const ZigZagPath = ({ levels = 8, target = 1, highlightColor = 'var(--purple-5)', spacing = 80, nodeR = 12 }) => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const height = isMobile ? 180 : 160;

  if (!isMobile) {
    const width = Math.max(300, (levels - 1) * spacing + 80);
    const positions = Array.from({ length: levels }).map((_, i) => {
      const x = 40 + i * spacing;
      const y = i % 2 === 0 ? 36 : height - 36;
      return { x, y };
    });
    const pathD = positions.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
      <div className="w-full flex justify-center my-6">
        <svg viewBox={`0 0 ${width} ${height}`} className="max-w-full h-40" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="zz-shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#000" floodOpacity="0.12" />
            </filter>
            <style>{`
              @keyframes zz-pulse { 0% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.18); opacity: .9 } 100% { transform: scale(1); opacity: 1 }
              }
            `}</style>
          </defs>

          <path d={pathD} fill="none" stroke="#E5E7EB" strokeWidth={8} strokeLinecap="round" />

          {positions.map((p, i) => {
            const idx = i + 1;
            const isTarget = idx === target;
            return (
              <g key={`zz-${i}`} transform={`translate(${p.x}, ${p.y})`}>
                <circle r={nodeR + 6} fill={isTarget ? highlightColor : '#E9EEF6'} opacity={isTarget ? 1 : 0.55} filter={isTarget ? 'url(#zz-shadow)' : undefined} />
                <circle r={nodeR} fill={isTarget ? '#fff' : '#F3F4F6'} stroke={isTarget ? highlightColor : '#CBD5E1'} strokeWidth={isTarget ? 2 : 1} style={isTarget ? { animation: 'zz-pulse 900ms ease-in-out infinite' } : {}} />
                <text x={0} y={p.y < height / 2 ? - (nodeR + 18) : (nodeR + 28)} textAnchor="middle" fontSize={12} fill={isTarget ? '#111827' : '#6B7280'} fontWeight={600}>{`L${idx}`}</text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  }

  // MOBILE layout: render two lines (top row then bottom row) while keeping single connected path
  const topCount = Math.ceil(levels / 2);
  const bottomCount = levels - topCount;
  const spacingMobile = Math.max(56, Math.floor((Math.max(320, topCount, bottomCount) * 1) + 40));
  const topPositions = Array.from({ length: topCount }).map((_, i) => ({ x: 24 + i * spacingMobile, y: 40 }));
  const bottomPositions = Array.from({ length: bottomCount }).map((_, j) => ({ x: 24 + j * spacingMobile, y: height - 40 }));

  // Combine path: top left->right, then down to first bottom, then bottom left->right
  const combined = [...topPositions, ...(bottomPositions.length ? [{ x: topPositions[topPositions.length - 1].x, y: topPositions[topPositions.length - 1].y }, { x: bottomPositions[0].x, y: bottomPositions[0].y }] : []), ...bottomPositions];
  const pathD = combined.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div className="w-full flex justify-center my-4 px-2">
      <svg viewBox={`0 0 ${Math.max(320, (Math.max(topCount, bottomCount) - 1) * spacingMobile + 80)} ${height}`} className="max-w-full h-40" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="zz-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#000" floodOpacity="0.12" />
          </filter>
          <style>{`
            @keyframes zz-pulse { 0% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.18); opacity: .9 } 100% { transform: scale(1); opacity: 1 }
            }
          `}</style>
        </defs>

        <path d={pathD} fill="none" stroke="#E5E7EB" strokeWidth={8} strokeLinecap="round" />

        {/* render top nodes */}
        {topPositions.map((p, i) => {
          const idx = i + 1;
          const isTarget = idx === target;
          return (
            <g key={`t-${i}`} transform={`translate(${p.x}, ${p.y})`}>
              <circle r={nodeR + 6} fill={isTarget ? highlightColor : '#E9EEF6'} opacity={isTarget ? 1 : 0.55} filter={isTarget ? 'url(#zz-shadow)' : undefined} />
              <circle r={nodeR} fill={isTarget ? '#fff' : '#F3F4F6'} stroke={isTarget ? highlightColor : '#CBD5E1'} strokeWidth={isTarget ? 2 : 1} style={isTarget ? { animation: 'zz-pulse 900ms ease-in-out infinite' } : {}} />
              <text x={0} y={- (nodeR + 12)} textAnchor="middle" fontSize={12} fill={isTarget ? '#111827' : '#6B7280'} fontWeight={600}>{`L${idx}`}</text>
            </g>
          );
        })}

        {/* render bottom nodes */}
        {bottomPositions.map((p, j) => {
          const idx = topCount + j + 1;
          const isTarget = idx === target;
          return (
            <g key={`b-${j}`} transform={`translate(${p.x}, ${p.y})`}>
              <circle r={nodeR + 6} fill={isTarget ? highlightColor : '#E9EEF6'} opacity={isTarget ? 1 : 0.55} filter={isTarget ? 'url(#zz-shadow)' : undefined} />
              <circle r={nodeR} fill={isTarget ? '#fff' : '#F3F4F6'} stroke={isTarget ? highlightColor : '#CBD5E1'} strokeWidth={isTarget ? 2 : 1} style={isTarget ? { animation: 'zz-pulse 900ms ease-in-out infinite' } : {}} />
              <text x={0} y={nodeR + 18} textAnchor="middle" fontSize={12} fill={isTarget ? '#111827' : '#6B7280'} fontWeight={600}>{`L${idx}`}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ZigZagPath;
